import React, { memo, useMemo, useState } from 'react';
import { makeStyles } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import RessourceApi from '../api/RessourceApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
  },
  shell: {
    maxWidth: '980px',
    margin: '0 auto',
  },
  card: {
    padding: theme.spacing(3),
    borderRadius: '14px',
    background: '#ffffff',
    border: '1px solid #e8edf3',
  },
  title: {
    marginTop: 0,
    marginBottom: theme.spacing(0.8),
    color: '#1a2f2a',
  },
  subtitle: {
    marginTop: 0,
    marginBottom: theme.spacing(2),
    color: '#4d5f58',
  },
  submitRow: {
    marginTop: theme.spacing(1),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  feedback: {
    marginTop: theme.spacing(1),
    fontSize: '0.9rem',
  },
  success: {
    color: '#0f766e',
  },
  error: {
    color: '#b91c1c',
  },
}));

const PilotageContenusContainer = memo(() => {
  const NB_MAX_IMAGES = 5;
  const classes = useStyles();
  const [typeContenu, setTypeContenu] = useState('');
  const [titre, setTitre] = useState('');
  const [resume, setResume] = useState('');
  const [contenuText, setContenuText] = useState('');
  const [estActif, setEstActif] = useState(true);
  const [fichiersImages, setFichiersImages] = useState([]);
  const [nomsImages, setNomsImages] = useState([]);
  const [cleInputFichier, setCleInputFichier] = useState(0);
  const [soumissionEnCours, setSoumissionEnCours] = useState(false);
  const [messageSucces, setMessageSucces] = useState('');
  const [messageErreur, setMessageErreur] = useState('');

  const optionsTriees = useMemo(() => {
    const options = [
      { label: 'Actualité', value: 'Actualite' },
      { label: 'Annonce', value: 'Annonce' },
      { label: 'Evenement', value: 'Evenement' },
      { label: 'Pratique', value: 'Pratique' },
      { label: 'Presentation', value: 'Presentation' },
      { label: 'Accueil', value: 'Navigation' },
    ];
    return options.sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
  }, []);

  const paragraphesContenu = useMemo(() => {
    return contenuText
      .split(/\n\s*\n/g)
      .map((paragraphe) => paragraphe.trim())
      .filter(Boolean);
  }, [contenuText]);

  const reinitialiserFormulaire = () => {
    setTypeContenu('');
    setTitre('');
    setResume('');
    setContenuText('');
    setEstActif(true);
    setFichiersImages([]);
    setNomsImages([]);
    setCleInputFichier((valeur) => valeur + 1);
  };

  const ajouterContenu = async () => {
    setMessageSucces('');
    setMessageErreur('');

    if (!typeContenu || !titre.trim() || !contenuText.trim()) {
      setMessageErreur('Veuillez renseigner le type, le titre et le contenu.');
      return;
    }

    if (fichiersImages.length > NB_MAX_IMAGES) {
      setMessageErreur(`Vous pouvez televerser au maximum ${NB_MAX_IMAGES} images par ressource.`);
      return;
    }

    setSoumissionEnCours(true);

    try {
      await RessourceApi.ajouter({
        categorie: typeContenu,
        titre: titre.trim(),
        resume: resume.trim(),
        description: contenuText.trim(),
        estActif,
        fichiers: fichiersImages,
      });

      setMessageSucces('Contenu enregistre avec succes.');
      reinitialiserFormulaire();
    } catch (error) {
      setMessageErreur("Echec de l'enregistrement du contenu.");
    } finally {
      setSoumissionEnCours(false);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.shell}>
        <Paper className={classes.card} elevation={0}>
          <h2 className={classes.title}>Pilotage des contenus</h2>
          <p className={classes.subtitle}>Selectionnez le type de contenu a piloter.</p>
          <Divider style={{ marginBottom: 20 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Type de contenu"
                value={typeContenu}
                onChange={(event) => setTypeContenu(event.target.value)}
              >
                <MenuItem value="" disabled>
                  Selectionnez un type de contenu
                </MenuItem>
                {optionsTriees.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Titre"
                value={titre}
                onChange={(event) => setTitre(event.target.value.slice(0, 50))}
                inputProps={{ maxLength: 50 }}
                helperText={`${titre.length}/50`}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Resume"
                value={resume}
                onChange={(event) => setResume(event.target.value.slice(0, 100))}
                inputProps={{ maxLength: 100 }}
                helperText={`${resume.length}/100`}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={8}
                label="Contenu text"
                value={contenuText}
                onChange={(event) => setContenuText(event.target.value.slice(0, 5000))}
                inputProps={{ maxLength: 5000 }}
                helperText={`Utilisez une ligne vide pour separer les paragraphes. ${contenuText.length}/5000`}
              />
            </Grid>
            {paragraphesContenu.length > 0 && (
              <Grid item xs={12}>
                <div style={{ color: '#1a2f2a', fontWeight: 600, marginBottom: 8 }}>
                  Apercu des paragraphes ({paragraphesContenu.length})
                </div>
                <div style={{ border: '1px solid #e8edf3', borderRadius: 10, padding: 12, background: '#f9fbfd', width: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
                  {paragraphesContenu.map((paragraphe, index) => (
                    <p key={index} style={{ margin: index === paragraphesContenu.length - 1 ? 0 : '0 0 12px 0', lineHeight: 1.6, color: '#2b3f38', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      {paragraphe}
                    </p>
                  ))}
                </div>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={estActif}
                    onChange={(event) => setEstActif(event.target.checked)}
                    color="primary"
                  />
                )}
                label="Est actif"
              />
            </Grid>
            <Grid item xs={12}>
              <input
                key={cleInputFichier}
                accept="image/*"
                id="upload-image-contenu"
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(event) => {
                  const fichiers = Array.from(event.target.files || []);
                  setFichiersImages(fichiers);
                  setNomsImages(fichiers.map((fichier) => fichier.name));

                  if (fichiers.length > NB_MAX_IMAGES) {
                    setMessageErreur(`Vous pouvez televerser au maximum ${NB_MAX_IMAGES} images par ressource.`);
                  } else {
                    setMessageErreur('');
                  }
                }}
              />
              <label htmlFor="upload-image-contenu">
                <Button variant="outlined" component="span">
                  Televerser des images (optionnel)
                </Button>
              </label>
              <div style={{ marginTop: 8, color: '#4d5f58', fontSize: '0.9rem' }}>
                {nomsImages.length > 0 ? `${nomsImages.length} image(s) selectionnee(s): ${nomsImages.join(', ')}` : 'Aucune image selectionnee'}
              </div>
              <div style={{ marginTop: 4, color: '#4d5f58', fontSize: '0.82rem' }}>
                Maximum {NB_MAX_IMAGES} images par ressource.
              </div>
            </Grid>
            <Grid item xs={12}>
              <div className={classes.submitRow}>
                <Button
                  variant="contained"
                  onClick={ajouterContenu}
                  disabled={soumissionEnCours}
                >
                  {soumissionEnCours ? 'Enregistrement...' : 'Ajouter le contenu'}
                </Button>
              </div>
              {messageSucces && <Typography className={`${classes.feedback} ${classes.success}`}>{messageSucces}</Typography>}
              {messageErreur && <Typography className={`${classes.feedback} ${classes.error}`}>{messageErreur}</Typography>}
            </Grid>
          </Grid>
        </Paper>
      </div>
    </div>
  );
});

export default PilotageContenusContainer;
